import { useState } from 'react';
import PropTypes from 'prop-types';

import useFocus from '../../utils/use-focus';
import { Divider } from '../Blade/styles';
import RegionLocking from '../RegionLocking';
import Blade from '../Blade';
import ModalWindow from '../ModalWindow';
import CtaButtonLink from '../CtaButtonLink';
import CtaTextLink from '../CtaTextLink';
import RichText from '../RichText';
import Image from '../Image';

import { BladeCtaContainer, BladeCtaImage, BladeCtaLinks } from './styles';

const BladeCtaSection = ({ data, themes }) => {
  const {
    headingRTF,
    paragraphRTF,
    ctaLinks,
    optionalImageDam,
    regionLocking,
  } = data;

  return (
    <RegionLocking provinces={regionLocking}>
      <BladeCtaContainer>
        {headingRTF && <RichText document={headingRTF} />}
        {optionalImageDam && (
          <BladeCtaImage>
            <Image damImage={optionalImageDam} />
          </BladeCtaImage>
        )}
        {paragraphRTF && <RichText document={paragraphRTF} />}
        {ctaLinks && (
          <BladeCtaLinks>
            {ctaLinks
              .filter(({ linkUrlMarkdown }) => linkUrlMarkdown)
              .map(
                ({
                  linkType,
                  style,
                  linkUrlMarkdown,
                  linkTextMarkdown,
                  linkText,
                  ariaLabel,
                  id,
                }) =>
                  linkType === 'Text-Link' ? (
                    <CtaTextLink
                      key={id}
                      contentId={id}
                      variant={style}
                      url={linkUrlMarkdown.linkUrlMarkdown}
                      ariaLabel={ariaLabel}
                      themes={themes}
                    >
                      {linkText || linkTextMarkdown.linkTextMarkdown}
                    </CtaTextLink>
                  ) : (
                    <CtaButtonLink
                      key={id}
                      contentId={id}
                      variant={style}
                      url={linkUrlMarkdown.linkUrlMarkdown}
                      ariaLabel={ariaLabel}
                    >
                      {linkText || linkTextMarkdown.linkTextMarkdown}
                    </CtaButtonLink>
                  )
              )}
          </BladeCtaLinks>
        )}
      </BladeCtaContainer>
      <Divider />
    </RegionLocking>
  );
};

BladeCtaSection.propTypes = {
  data: PropTypes.shape().isRequired,
  themes: PropTypes.string,
};

BladeCtaSection.defaultProps = {
  themes: 'light',
};

const Modal = ({ className, data, children, themes }) => {
  const {
    linkText,
    linkType,
    style,
    content,
    ariaLabel,
    component: modalType,
  } = data;

  const [active, setActive] = useState(false);
  const onClose = () => setActive(false);
  const [elementRef, setElementRef] = useFocus();

  return (
    <>
      {linkType === 'text-link' && (
        <CtaTextLink
          ref={elementRef}
          variant={style}
          onClick={() => setActive(true)}
          className={className}
          ariaLabel={ariaLabel}
          themes={themes}
        >
          {children || linkText}
        </CtaTextLink>
      )}
      {linkType === 'button' && (
        <CtaButtonLink
          ref={elementRef}
          variant={style}
          onClick={() => setActive(true)}
          className={className}
          ariaLabel={ariaLabel}
        >
          {children || linkText}
        </CtaButtonLink>
      )}

      {modalType === 'Modal Blade' && (
        <Blade
          active={active}
          onClose={onClose}
          elementRef={elementRef}
          setElementRef={setElementRef}
        >
          {content.map(({ id, ...ctaSection }) => (
            <BladeCtaSection key={id} data={ctaSection} themes={themes} />
          ))}
        </Blade>
      )}
      {modalType === 'Modal Window' && (
        <ModalWindow
          active={active}
          onClose={onClose}
          data={data}
          elementRef={elementRef}
          setElementRef={setElementRef}
          themes={themes}
        />
      )}
    </>
  );
};

Modal.propTypes = {
  data: PropTypes.shape().isRequired,
  className: PropTypes.string,
  children: PropTypes.node,
  themes: PropTypes.string,
};

Modal.defaultProps = {
  className: '',
  children: '',
  themes: 'light',
};

export default Modal;
